import {
  Box,
  Chip,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { DocumentResponseDto } from "../../api/documentsApi";
import { Action, Column } from "../common/DataTable/DataTable";
import {
  formatFileSize,
  getFileTypeName,
  isImageFile,
  isPdfFile,
} from "../../utils/fileUtils";

interface DocumentColumnOptions {
  includeStatusColumn?: boolean;
  includePermitColumn?: boolean;
}

interface DocumentActionOptions {
  isDeleting?: boolean;
  onDelete?: (document: DocumentResponseDto) => void;
  onDownload?: (document: DocumentResponseDto) => void;
  onEdit?: (document: DocumentResponseDto) => void;
  onView?: (document: DocumentResponseDto) => void;
}

const getFileTypeColor = (
  contentType: string
): "error" | "primary" | "info" | "success" | "default" => {
  if (isPdfFile(contentType)) return "error";
  if (isImageFile(contentType)) return "primary";
  if (contentType.includes("document") || contentType.includes("word")) {
    return "info";
  }
  if (contentType.includes("sheet") || contentType.includes("excel")) {
    return "success";
  }
  return "default";
};

const getFileTypeIcon = (contentType: string) => {
  if (isPdfFile(contentType)) return <PdfIcon />;
  if (isImageFile(contentType)) return <ImageIcon />;
  return <DocumentIcon />;
};

export const isDocumentExpiringSoon = (expirationDate?: string) => {
  if (!expirationDate) return false;
  const expDate = new Date(expirationDate);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return expDate <= thirtyDaysFromNow && expDate > now;
};

export const isDocumentExpired = (expirationDate?: string) => {
  if (!expirationDate) return false;
  return new Date(expirationDate) < new Date();
};

export const getDocumentRowSx = (
  document: DocumentResponseDto
): SxProps<Theme> => ({
  ...(isDocumentExpired(document.expirationDate) && {
    backgroundColor: "error.light",
    "&:hover": { backgroundColor: "error.main" },
  }),
  ...(isDocumentExpiringSoon(document.expirationDate) &&
    !isDocumentExpired(document.expirationDate) && {
      backgroundColor: "warning.light",
      "&:hover": { backgroundColor: "warning.main" },
    }),
});

export const createDocumentColumns = ({
  includeStatusColumn = true,
  includePermitColumn = true,
}: DocumentColumnOptions = {}): Column[] => {
  const columns: Column[] = [
    {
      field: "originalFileName",
      headerName: "Document",
      tooltipValue: (_value, document: DocumentResponseDto) => (
        <Box sx={{ whiteSpace: "pre-line" }}>
          {[document.originalFileName, document.description]
            .filter(Boolean)
            .join("\n")}
        </Box>
      ),
      renderCell: (_value, document: DocumentResponseDto) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getFileTypeIcon(document.contentType)}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {document.originalFileName}
            </Typography>
            {document.description && (
              <Typography variant="caption" color="text.secondary">
                {document.description}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "contentType",
      headerName: "Type",
      renderCell: (value) => (
        <Chip
          label={getFileTypeName(value)}
          size="small"
          color={getFileTypeColor(value)}
          variant="outlined"
        />
      ),
    },
    {
      field: "documentCategory",
      headerName: "Category",
      renderCell: (value) => (
        <Typography variant="body2">{value || "-"}</Typography>
      ),
    },
    {
      field: "fileSizeBytes",
      headerName: "Size",
      renderCell: (value) => (
        <Typography variant="body2">{formatFileSize(value)}</Typography>
      ),
    },
    {
      field: "uploadedAt",
      headerName: "Uploaded",
      renderCell: (value) => (
        <Box>
          <Typography variant="body2">
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(value).toLocaleDateString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: "expirationDate",
      headerName: "Expiration",
      renderCell: (value) =>
        value ? (
          <Box>
            <Typography
              variant="body2"
              color={
                isDocumentExpired(value)
                  ? "error"
                  : isDocumentExpiringSoon(value)
                    ? "warning.dark"
                    : "text.primary"
              }
            >
              {new Date(value).toLocaleDateString()}
            </Typography>
            {(isDocumentExpired(value) || isDocumentExpiringSoon(value)) && (
              <Typography
                variant="caption"
                color={isDocumentExpired(value) ? "error" : "warning.dark"}
              >
                {isDocumentExpired(value) ? "Expired" : "Expires soon"}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No expiration
          </Typography>
        ),
    },
  ];

  if (includeStatusColumn) {
    columns.push({
      field: "isActive",
      headerName: "Status",
      renderCell: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          color={value ? "success" : "default"}
          variant={value ? "filled" : "outlined"}
        />
      ),
    });
  }

  if (includePermitColumn) {
    columns.push({
      field: "isPermit",
      headerName: "Permit",
      renderCell: (value) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          color={value ? "primary" : "default"}
          variant={value ? "filled" : "outlined"}
        />
      ),
    });
  }

  return columns;
};

export const createDocumentActions = ({
  isDeleting = false,
  onDelete,
  onDownload,
  onEdit,
  onView,
}: DocumentActionOptions): Action<DocumentResponseDto>[] => {
  const actions: Action<DocumentResponseDto>[] = [];

  if (onView) {
    actions.push({
      icon: <ViewIcon fontSize="small" />,
      label: "View Document",
      onClick: onView,
      color: "primary",
    });
  }

  if (onDownload) {
    actions.push({
      icon: <DownloadIcon fontSize="small" />,
      label: "Download Document",
      onClick: onDownload,
      color: "primary",
    });
  }

  if (onEdit) {
    actions.push({
      icon: <EditIcon fontSize="small" />,
      label: "Edit / Replace Document",
      onClick: onEdit,
      color: "secondary",
    });
  }

  if (onDelete) {
    actions.push({
      icon: <DeleteIcon fontSize="small" />,
      label: "Delete Document",
      onClick: onDelete,
      color: "error",
      disabled: isDeleting,
    });
  }

  return actions;
};
